import clsx from 'clsx';
import type { ReactNode } from 'react';

type Tone =
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'accent'
  | 'muted';

const TONE: Record<Tone, string> = {
  success: 'bg-success/15 text-success border-success/30',
  danger: 'bg-danger/15 text-danger border-danger/30',
  warning: 'bg-warning/15 text-warning border-warning/30',
  info: 'bg-info/15 text-info border-info/30',
  accent: 'bg-accent/15 text-accent border-accent/30',
  muted: 'bg-white/[0.06] text-[var(--text-muted)] border-white/10',
};

const STATUS_TONE: Record<string, Tone> = {
  Approved: 'success',
  Rejected: 'danger',
  Pending: 'warning',
  Draft: 'muted',
  'Pending HOD': 'warning',
  'HOD Approved': 'info',
  'Director Approved': 'success',
  'In Stock': 'success',
  Shortage: 'warning',
  'Out of Stock': 'danger',
  'Not Stocked Yet': 'muted',
  Sent: 'info',
  Acknowledged: 'info',
  Delivered: 'success',
  Cancelled: 'danger',
  Active: 'success',
  Inactive: 'muted',
  'RFQ Sent': 'info',
  'Quotes Received': 'accent',
  'PO Generated': 'success',
  'Low Stock': 'warning',
  'Received in Stock': 'success',
  Issued: 'success',
  Partial: 'warning',
  'Quality Checked': 'info',
  Stored: 'success',
  'In Transit': 'info',
  Fulfilled: 'success',
  Closed: 'muted',
  Open: 'success',
};

const PRIORITY_TONE: Record<string, Tone> = {
  Low: 'muted',
  Medium: 'info',
  High: 'warning',
  Urgent: 'danger',
};

export function Badge({
  children,
  tone,
}: {
  children: ReactNode;
  tone?: Tone;
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold tracking-wide',
        TONE[tone ?? 'muted'],
      )}
    >
      {children}
    </span>
  );
}

export function StatusBadge({ status }: { status: string }) {
  return <Badge tone={STATUS_TONE[status] ?? 'muted'}>{status}</Badge>;
}

export function PriorityBadge({ priority }: { priority: string }) {
  return <Badge tone={PRIORITY_TONE[priority] ?? 'muted'}>{priority}</Badge>;
}
